/**
 * EMZI NEXUS V6.3 - WhatsApp Utilities
 * Purpose: Generate WhatsApp links and format messages
 */

/**
 * Extract digits only from E.164 phone number
 */
export const extractPhoneDigits = (phone_e164) => {
  if (!phone_e164) return '';
  return phone_e164.replace(/\D/g, '');
};

/**
 * Generate WhatsApp wa.me link
 */
export const generateWhatsAppLink = (phone_e164, message) => {
  const digits = extractPhoneDigits(phone_e164);
  if (!digits) return '';
  
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${digits}?text=${encodedMessage}`;
};

/**
 * Interpolate message template with user data
 */
export const interpolateMessage = (template, data) => {
  let message = template;
  
  // Replace placeholders
  Object.keys(data).forEach(key => {
    const placeholder = `{${key}}`;
    message = message.replace(new RegExp(placeholder, 'g'), data[key] || '');
  });
  
  return message;
};

/**
 * Get default WhatsApp message template for bulk notifications
 */
export const getDefaultMessageTemplate = () => {
  return `Hi {full_name},

Your EMZI Nexus account is active.

Email: {email}
Login: {login_url}

– EMZI Nexus Team`;
};

/**
 * Get default WhatsApp message template for new user
 */
export const getNewUserMessageTemplate = (email, temp_password, full_name, login_url) => {
  const firstName = full_name.split(' ')[0] || 'User';
  
  return `Hi ${firstName},

Your EMZI Nexus account is ready.

Email: ${email}
Temporary Password: ${temp_password}

Login: ${login_url}
You'll be asked to change this password on first login.

– EMZI Nexus Team`;
};

/**
 * Open multiple WhatsApp links sequentially with delay
 */
export const openWhatsAppLinksSequentially = (links, delayMs = 500) => {
  links.forEach((link, index) => {
    setTimeout(() => {
      window.open(link, '_blank');
    }, index * delayMs);
  });
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (err) {
      document.body.removeChild(textArea);
      return false;
    }
  }
};

/**
 * Generate CSV content from created users data
 */
export const generateCreatedUsersCSV = (users) => {
  const headers = ['email', 'full_name', 'role', 'phone_e164', 'temp_password', 'whatsapp_link', 'login_url'];
  const rows = [headers.join(',')];
  
  users.forEach(user => {
    const row = [
      user.email || '',
      user.full_name || '',
      user.role || '',
      user.phone_e164 || '',
      user.temp_password || '',
      `"${user.whatsapp_link || ''}"`, // Quote URLs
      `"${user.login_url || ''}"`
    ];
    rows.push(row.join(','));
  });
  
  return rows.join('\n');
};

/**
 * Download CSV file
 */
export const downloadCSV = (content, filename) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
